import { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Typography, notification } from 'antd';
import Header from '../../components/Header';
import { Link, useNavigate } from 'react-router-dom';
import { useLazyGetQueueTotalQuery } from '../../redux/api/order-line-item.ts';
import { useGetOrderCountsQuery } from '../../redux/api/order';

import {
	CheckCircleOutlined,
	SyncOutlined,
	InboxOutlined,
} from '@ant-design/icons';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	LabelList,
} from 'recharts';

import { queueOverviewData } from '../../config/constants';

const { Title } = Typography;

const orderStatus = {
	received: 12,
	inProgress: 8,
	completed: 20,
};

interface IQueueData {
	name: string;
	total: number;
	field: string;
	url: string;
}

const Dashboard = () => {
	const [queueData, setQueueData] = useState<IQueueData[]>([]);
	const navigate = useNavigate();
	const [getQueueTotalQuery] = useLazyGetQueueTotalQuery();
	const { data, isLoading, isFetching, refetch } = useGetOrderCountsQuery();
	const [queueLoading, setQueueLoading] = useState(false);

	const fetchQueueData = async () => {
		setQueueLoading(true);
		await getQueueTotalQuery({})
			.unwrap()
			.then((res) => {
				setQueueLoading(false);
				console.log(res);
				if (res) {
					const formattedData = queueOverviewData?.map((item: any) => {
						if (res && item?.field in res) {
							return {
								...item,
								total: res?.[item?.field],
							};
						}
						return item;
					});
					setQueueData(formattedData);
				}
			})
			.catch((error) => {
				setQueueLoading(false);
				notification.error({
					duration: 10,
					message: 'Error',
					description: `Error encountered while attempting to fetch. ${error?.data?.detail}`,
					placement: 'topRight',
				});
			});
	};

	useEffect(() => {
		fetchQueueData();
	}, []);

	return (
		<div>
			<Header
				title="Dashboard"
				breadcrumbItems={[
					{
						title: <Link to="/">Home</Link>,
					},
				]}
			/>
			<Title level={4} className="mt-10">
				Production
			</Title>

			<Row gutter={16}>
				<Col span={8}>
					<Card>
						<Statistic
							title="Received Orders"
							value={data?.received_count}
							prefix={<InboxOutlined style={{ color: '#1890ff' }} />}
							loading={isLoading || isFetching}
						/>
						<div style={{ marginTop: 8 }}>
							<Link to="/orders?status=Received">View All</Link>
						</div>
					</Card>
				</Col>

				<Col span={8}>
					<Card>
						<Statistic
							title="In Progress Orders"
							value={data?.in_progress_count}
							prefix={<SyncOutlined style={{ color: '#faad14' }} spin />}
							loading={isLoading || isFetching}
						/>
						<div style={{ marginTop: 8 }}>
							<Link to="/orders?status=In+Progress">View All</Link>
						</div>
					</Card>
				</Col>

				<Col span={8}>
					<Card>
						<Statistic
							title="Completed Orders"
							value={data?.completed_count}
							prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
							loading={isLoading || isFetching}
						/>
						<div style={{ marginTop: 8 }}>
							<Link to="/orders?status=Completed">View All</Link>
						</div>
					</Card>
				</Col>
			</Row>

			<Title level={4} className="mt-10">
				Queue Overview
			</Title>

			<Row gutter={16}>
				{queueData?.map((item) => (
					<>
						<Col span={4}>
							<Card
								style={{
									marginBottom: '20px',
								}}
								styles={{
									body: {
										padding: '15px',
									},
								}}
								loading={queueLoading}
							>
								<Statistic
									title={item.name}
									value={item.total}
									prefix={<InboxOutlined style={{ color: '#1890ff' }} />}
									loading={queueLoading}
								/>
								<div style={{ marginTop: 8 }}>
									<Link to={`${item?.url}`}>View All</Link>
								</div>
							</Card>
						</Col>
					</>
				))}
			</Row>

			<Row gutter={16} style={{ marginTop: 32 }}>
				<Col span={24}>
					<Card title="Queue Overview" loading={queueLoading}>
						<ResponsiveContainer width="100%" height={300}>
							<BarChart data={queueData}>
								<XAxis dataKey="name" />
								<YAxis />
								<Tooltip />
								<Bar
									dataKey="total"
									name="Total"
									fill="#8884d8"
									onClick={(data, index) => {
										// Handle navigation here
										navigate(`${data?.url}`); // Using react-router
									}}
								>
									<LabelList
										dataKey="total"
										position="center"
										fill="#fff" // White text for contrast
										// formatter={(value) => value.toLocaleString()} // Optional formatting
									/>
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default Dashboard;
